import { DateTime } from 'luxon';

import { BenefitDto } from 'theSource/queries';
import {
  DECLINED_COVERAGE_PLAN_LOOKUP_CODE,
  MEDICAL_BENEFIT_CATEGORY,
  HEALTH_WELLNESS_BENEFIT_CATEGORY,
  WEALTH_FINANCIAL_BENEFIT_CATEGORY,
  LIFESTYLE_BENEFIT_CATEGORY,
  API_DATE_FORMAT,
  INFINITE_END_DATE,
} from 'theSource/utils';
import {
  Benefit,
  BenefitCategoryNullable,
  BenefitCategoryWithValue,
  BenefitsWithValue,
} from '../types';

export const mapBenefits = (
  benefits: BenefitDto[],
  orgInformationalBenefits: BenefitDto[]
): Benefit[] => {
  const combinedBenefits: Benefit[] = [];
  // Filter out surveys and agreements that weren't added as additional benefits
  const filteredBenefits = benefits.filter((benefit) => !((benefit.benefit_lookup_code.startsWith('survey') ||
      benefit.benefit_lookup_code.startsWith('agreement')) && !benefit.benefit_lookup_code.endsWith('_additional_benefits')));
  filteredBenefits.forEach((benefit) => {
    combinedBenefits.push({
      displayOrder: benefit.display_order,
      orgBenefitName: benefit.org_benefit_name,
      benefitLookupCode: benefit.benefit_lookup_code,
      benefitCategory: benefit.benefit_category,
      coverage: {
        planLookupCode: benefit.coverage.plan_lookup_code,
      },
    });
  });
  orgInformationalBenefits.forEach((orgInformationalBenefit) => {
    if (
      dateValidator(orgInformationalBenefit) &&
      !orgInformationalBenefit.hidden
    ) {
      combinedBenefits.push({
        displayOrder: 10000,
        orgBenefitName: orgInformationalBenefit.org_benefit_name,
        benefitLookupCode: orgInformationalBenefit.benefit_lookup_code,
        benefitCategory: orgInformationalBenefit.benefit_category,
        coverage: {
          planLookupCode: orgInformationalBenefit.benefit_lookup_code,
        },
      });
    }
  });
  return combinedBenefits;
};

export const dateValidator = (orgInformationalBenefit: BenefitDto): Boolean => {
  return (
    DateTime.fromFormat(
      orgInformationalBenefit.starts_on,
      API_DATE_FORMAT
    ).toISO() <= currentDate &&
    currentDate <=
      DateTime.fromFormat(
        orgInformationalBenefit.ends_on || INFINITE_END_DATE,
        API_DATE_FORMAT
      ).toISO()
  );
};

const currentDate = DateTime.now()
  .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
  .toISO();

export const mapCategories = (
  benefits: Benefit[],
  categories: BenefitCategoryNullable[]
): BenefitCategoryWithValue[] => {
  const mappedCategories = [...categories];
  benefits.forEach((benefit) => {
    const MEDICAL_CATEGORY_INDEX = 0;
    const HEALTH_WELLNESS_CATEGORY_INDEX = 1;
    const WEALTH_FINANCIAL_CATEGORY_INDEX = 2;
    const LIFESTYLE_CATEGORY_INDEX = 3;
    const DISABLED_CATEGORY_INDEX = 4;
    const benefitLookupCode = benefit.benefitLookupCode;
    const benefitCategory = benefit.benefitCategory;
    if (
      benefit.coverage.planLookupCode === DECLINED_COVERAGE_PLAN_LOOKUP_CODE
    ) {
      mappedCategories[DISABLED_CATEGORY_INDEX].benefits[benefitLookupCode] =
        benefit;
    } else if (benefitCategory === MEDICAL_BENEFIT_CATEGORY) {
      mappedCategories[MEDICAL_CATEGORY_INDEX].benefits[benefitLookupCode] =
        benefit;
    } else if (benefitCategory === HEALTH_WELLNESS_BENEFIT_CATEGORY) {
      mappedCategories[HEALTH_WELLNESS_CATEGORY_INDEX].benefits[
        benefitLookupCode
      ] = benefit;
    } else if (benefitCategory === WEALTH_FINANCIAL_BENEFIT_CATEGORY) {
      mappedCategories[WEALTH_FINANCIAL_CATEGORY_INDEX].benefits[
        benefitLookupCode
      ] = benefit;
    } else if (benefitCategory === LIFESTYLE_BENEFIT_CATEGORY) {
      mappedCategories[LIFESTYLE_CATEGORY_INDEX].benefits[benefitLookupCode] =
        benefit;
    }
  });
  return filterCategories(mappedCategories);
};

const sortBenefits = (benefits: BenefitsWithValue) =>
  Object.entries(benefits)
    .sort(
      ([, benefitOne], [, benefitTwo]) =>
        benefitOne.displayOrder - benefitTwo.displayOrder
    )
    .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

const filterCategories = (
  categories: BenefitCategoryNullable[]
): BenefitCategoryWithValue[] =>
  categories.map((category) => {
    const mappedBenefits: BenefitsWithValue = {};
    Object.entries(category.benefits).forEach(([key, value]) => {
      if (value) {
        mappedBenefits[key] = value;
      }
    });
    const mappedCategory: BenefitCategoryWithValue = {
      ...category,
      benefits: sortBenefits(mappedBenefits),
    };

    return mappedCategory;
  });
